import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'

const MAX_HEIGHT = 1000

interface ResizableChartProps {
  storageKey: string
  label: string
  defaultHeight: number
  minHeight: number
  collapsed: boolean
  height?: number
  onHeightChange?: (height: number) => void
  children: (height: number) => ReactNode
}

function clampHeight(height: number, minHeight: number) {
  return Math.round(Math.min(MAX_HEIGHT, Math.max(minHeight, height)))
}

function availableChartHeight(target?: HTMLElement) {
  const viewport = target?.closest<HTMLElement>('[data-chart-resize-viewport]')
  return Math.max(1, viewport?.clientHeight || (typeof window === 'undefined' ? 1000 : window.innerHeight) || 1000)
}

function heightStops(reference: number, minHeight: number): number[] {
  const step = Math.max(1, reference / 20)
  const values = [minHeight, MAX_HEIGHT]
  for (let index = Math.ceil(minHeight / step); index <= Math.floor(MAX_HEIGHT / step); index += 1) {
    values.push(Math.round(index * step))
  }
  return [...new Set(values)].sort((a, b) => a - b)
}

const snappedHeight = (height: number, stops: number[]) => stops.reduce((nearest, stop) =>
  Math.abs(stop - height) <= Math.abs(nearest - height) ? stop : nearest)

function readHeight(storageKey: string, defaultHeight: number, minHeight: number) {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(storageKey) || 'null')
    if (typeof saved === 'number' && Number.isFinite(saved)) return clampHeight(saved, minHeight)
  } catch { /* Browser preferences are optional. */ }
  return defaultHeight
}

/** Each pane keeps its size across tickers, intervals, view modes, and reloads. */
export function ResizableChart(props: ResizableChartProps) {
  return <ResizableChartPane key={props.storageKey} {...props} />
}

function ResizableChartPane({ storageKey, label, defaultHeight, minHeight, collapsed, height: controlledHeight, onHeightChange, children }: ResizableChartProps) {
  const [localHeight, setLocalHeight] = useState(() => readHeight(storageKey, defaultHeight, minHeight))
  const [previewHeight, setPreviewHeight] = useState<number | null>(null)
  const savedHeight = controlledHeight === undefined ? localHeight : clampHeight(controlledHeight, minHeight)
  const height = previewHeight ?? savedHeight
  const setHeight = onHeightChange || setLocalHeight
  const saveHeight = (value: number) => {
    setHeight(value)
    if (onHeightChange) return
    try { localStorage.setItem(storageKey, JSON.stringify(value)) } catch { /* Keep resizing without storage. */ }
  }

  return (
    <div className="relative min-w-0" data-resizable-chart={label}>
      {children(height)}
      {!collapsed && <ChartHeightHandle label={label} height={height} minHeight={minHeight}
        defaultHeight={defaultHeight} onPreview={setPreviewHeight}
        onCommit={(next) => { setPreviewHeight(null); if (next !== savedHeight) saveHeight(next) }} />}
    </div>
  )
}

function ChartHeightHandle({ label, height, minHeight, defaultHeight, onPreview, onCommit }: {
  label: string
  height: number
  minHeight: number
  defaultHeight: number
  onPreview: (height: number | null) => void
  onCommit: (height: number) => void
}) {
  const dragRef = useRef<{
    pointerId: number
    startY: number
    startHeight: number
    height: number
    stops: number[]
    moved: boolean
    restore: () => void
  } | null>(null)
  const frameRef = useRef<number | null>(null)
  const [dragging, setDragging] = useState(false)

  useEffect(() => () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    const drag = dragRef.current
    dragRef.current = null
    drag?.restore()
  }, [])

  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return
    drag.moved ||= event.clientY !== drag.startY
    drag.height = drag.moved ? snappedHeight(drag.startHeight + event.clientY - drag.startY, drag.stops) : drag.startHeight
    if (frameRef.current === null) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null
        if (dragRef.current) onPreview(dragRef.current.height)
      })
    }
  }
  const finishDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return
    // Capture the release position even when it precedes the next animation frame.
    if (event.type === 'pointerup') {
      drag.moved ||= event.clientY !== drag.startY
      drag.height = drag.moved ? snappedHeight(drag.startHeight + event.clientY - drag.startY, drag.stops) : drag.startHeight
    }
    dragRef.current = null
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    frameRef.current = null
    drag.restore()
    setDragging(false)
    if (event.type === 'pointerup') onCommit(drag.height)
    else onPreview(null)
  }

  return (
    <div
      role="separator"
      aria-label={`Resize ${label} height`}
      aria-orientation="horizontal"
      aria-valuemin={minHeight}
      aria-valuemax={MAX_HEIGHT}
      aria-valuenow={height}
      aria-valuetext={`${height} pixels`}
      tabIndex={0}
      title="Drag or use arrow keys: 5% steps of the chart area height · Double-click to reset"
      data-height-dragging={dragging ? 'true' : undefined}
      className={`group relative z-20 flex h-2.5 w-full cursor-row-resize touch-none select-none items-center justify-center rounded outline-none hover:bg-teal-500/15 focus-visible:bg-teal-500/20 ${dragging ? 'bg-teal-500/20' : ''}`}
      onPointerDown={(event) => {
        if (event.button !== 0 || !event.isPrimary || dragRef.current) return
        event.preventDefault()
        event.stopPropagation()
        const target = event.currentTarget
        const pointerId = event.pointerId
        target.focus({ preventScroll: true })
        target.setPointerCapture(pointerId)
        const { cursor, userSelect } = document.body.style
        document.body.style.cursor = 'row-resize'
        document.body.style.userSelect = 'none'
        dragRef.current = {
          pointerId, startY: event.clientY, startHeight: height, height, moved: false,
          stops: heightStops(availableChartHeight(target), minHeight),
          restore: () => {
            document.body.style.cursor = cursor
            document.body.style.userSelect = userSelect
            if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId)
          },
        }
        setDragging(true)
      }}
      onPointerMove={moveDrag}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onLostPointerCapture={finishDrag}
      onDoubleClick={(event) => { event.stopPropagation(); onCommit(defaultHeight) }}
      onKeyDown={(event) => {
        if (dragRef.current || !['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return
        event.preventDefault()
        event.stopPropagation()
        const stops = heightStops(availableChartHeight(event.currentTarget), minHeight)
        const value = event.key === 'Home' ? minHeight : event.key === 'End' ? MAX_HEIGHT
          : event.key === 'ArrowDown' ? stops.find(stop => stop > height) ?? MAX_HEIGHT
            : [...stops].reverse().find(stop => stop < height) ?? minHeight
        onCommit(value)
      }}
    >
      <span className={`h-0.5 w-10 rounded-full group-hover:bg-teal-400 group-focus-visible:bg-teal-400 ${dragging ? 'bg-teal-400' : 'bg-slate-600/70'}`} />
    </div>
  )
}
